'use client';

import { type FC, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AnalyticsProvider } from '@naumu/analytics/analytics/analytics-context';
import { AnalyticsConsentBanner } from '@naumu/analytics/consent/analytics-consent-banner';
import { SignupAttributionReporter } from '@naumu/analytics/attribution/signup-attribution-reporter';
import { posthogAnalyticsClient } from '@naumu/analytics/posthog/posthog-provider';
import { ThemeProviderMinimal } from '@naumu/ui/lib/theme/theme-provider-minimal';
import { ViewportSizeProvider } from '@naumu/ui/lib/viewport-size/index';
import { MousePositionProvider } from '@naumu/ui/lib/use-mouse-position';
import { useDisableSwipeNav } from '@naumu/ui/lib/use-disable-swipe-nav';

import { NextLinkBridge } from '@/components/app-link';
import { TouchAttributionProvider } from '@/lib/touch-attribution-provider';
import { configurePlatformClient } from '@naumu/platform-client/platform-client-config';

// Tell `@naumu/platform-client` (shared with `apps/web`) which app it is
// running in, so entering a space resolves to the PLATFORM origin - which in
// local dev is a different port than this one. Module scope, not an effect: the
// value is constant for the bundle's lifetime and must be set before any CTA
// can fire. See `platform-client-config.ts`.
configurePlatformClient({ app: 'marketing' });

interface MinimalProvidersProps {
	children: React.ReactNode;
}

/**
 * Providers for the marketing app.
 *
 * twin-of: apps/web/app/providers-minimal.tsx - same provider stack in the
 * same order, kept identical apart from the deltas listed below. Web resolves
 * the shared providers through `@/lib/*`; here they come from `@naumu/ui`.
 *
 * Deliberately smaller than the web app's same-named component. Marketing has
 * no auth, no websocket, and no PWA surface, so three of web's providers are
 * absent on purpose:
 *
 *  - `ServiceWorkerRegistrar`: marketing must NOT register a service worker.
 *    `/sw.js` belongs to the platform origin and caches `/_next/static/*`;
 *    registering it from a marketing page (same hostname, different container)
 *    would put the platform SW in front of `/mkt/_next/*` requests it knows
 *    nothing about. This image ships no `sw.js` at all.
 *  - `PersistQueryClientProvider`: nothing here reads a persisted query. The
 *    one React Query touch point is the guest-provisioning cache seed, and the
 *    navigation that follows it leaves this app entirely.
 *  - `NativeShellBootGate` lives on the landing route, not here, exactly as in
 *    web.
 *
 * The QueryClient defaults that DO matter (staleTime, gcTime) match web's, so a
 * component moved between the two behaves identically.
 */
export const MinimalProviders: FC<MinimalProvidersProps> = ({ children }) => {
	useDisableSwipeNav();
	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						staleTime: 60 * 1000,
						retry: 1,
						gcTime: 30 * 60 * 1000,
					},
				},
			}),
	);

	return (
		<QueryClientProvider client={queryClient}>
			<NextLinkBridge>
				<AnalyticsProvider client={posthogAnalyticsClient}>
					{/*
					  Marketing attribution. Ordered before the tree so the touch
					  record is written first and the signup reporter (which reads
					  it) runs second. Both render null.
					*/}
					<TouchAttributionProvider />
					<SignupAttributionReporter />
					<ThemeProviderMinimal>
						<ViewportSizeProvider>
							<MousePositionProvider>
								{children}
								<AnalyticsConsentBanner />
							</MousePositionProvider>
						</ViewportSizeProvider>
					</ThemeProviderMinimal>
				</AnalyticsProvider>
			</NextLinkBridge>
		</QueryClientProvider>
	);
};
